/**
 * This module provides functions in relation to calling API routes on the backend server and processing the incoming
 * responses. The functions in this module refer to the API endpoint ApiEndPoints.TIME_SERIES.
 * @module
 */

import {ApiEndPoints, HttpMethods} from "../base/Settings";
import type {FetchRequest, TimeSeriesInput, TimeSeries, UUID} from "../base/ui_general/GeneralInterfaces";


/**
 * Creates a FetchRequest object populated with the parameters necessary for triggering the creation of a new
 * time_series on the server. Only name, description and time_series type need to be specified for this purpose.
 * @param time_seriesInput An object of type TimeSeriesInput containing the input data for creating the time_series.
 * @returns A FetchRequest object containing the generated fetch request.*/
export function getRequest_create_time_series(time_seriesInput: TimeSeriesInput): FetchRequest {
    return {
        api_endpoint: ApiEndPoints.TIME_SERIES,
        method: HttpMethods.POST,
        query_parameters: {},
        body: time_seriesInput,
    }
}


/**
 * Creates a FetchRequest object populated with the parameters necessary for updating an existing
 * time_series on the server.
 * @param time_series An object of type TimeSeries containing the updated time_series data.
 * @returns A FetchRequest object containing the generated fetch request.
 */
export function getRequest_update_time_series(time_series: TimeSeries): FetchRequest {
    const timeseriesPayload = Object.fromEntries(
        Object.entries(time_series).filter(
            ([key]) => key !== "time_series_rows" && key !== "interpolated_rows"
        )
    );
    return {
        api_endpoint: ApiEndPoints.TIME_SERIES,
        method: HttpMethods.PUT,
        query_parameters: {},
        body: timeseriesPayload,
    };
}


/**
 * Creates a FetchRequest object populated with the parameters necessary for triggering the query of time_seriess
 * on the server.
 * @param transaction_id .The UUID of the transaction to query for.
 * @returns A FetchRequest object containing the generated fetch request.*/
export function getRequest_get_all_time_series(transaction_id: UUID): FetchRequest {
    return {
        api_endpoint: ApiEndPoints.TIME_SERIES,
        method: HttpMethods.GET,
        query_parameters: { transaction_id: transaction_id },
    }
}


/**
 * Creates a FetchRequest object populated with the parameters necessary for deleting an existing
 * time_series on the server.
 * @param time_series_id The UUID of the time_series to delete.
 * @returns A FetchRequest object containing the generated fetch request.
 */
export function getRequest_delete_time_series(time_series_id: UUID): FetchRequest {
    return {
        api_endpoint: ApiEndPoints.TIME_SERIES,
        method: HttpMethods.DELETE,
        query_parameters: { time_series_id: time_series_id },
    };
}
